import axios from 'axios';
import apiConfig from '../apiConfig.ts';

const API_BASE_URL = apiConfig.apiBaseURL;

// Interfaces
export interface SubscriptionTransaction {
  _id?: string;
  id?: string;
  userId?: string;
  subscriptionId?: string;
  amount?: number;
  amountPaid?: number;
  status: string;
  planType?: string;
  billingCycle?: string;
  subscriptionType?: string;
  subscriptionPlanName?: string;
  startDate: string;
  endDate: string;
  customerName?: string;
  userName?: string;
  customerEmail?: string;
  email?: string;
  transactionId: string;
  transactionIdLast5?: string;
  transactionId8Digit?: string;
  coupon?: string | null;
  discount?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SubscriptionPlan {
  _id: string;
  name: string;
  description: string;
  priceMonthly: number;
  priceYearly: number;
  features: string[];
  planType: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionCounts {
  totalSubscriptions: number;
  activeSubscriptions: number;
  inactiveSubscriptions: number;
}

// API Response interfaces
export interface SubscriptionPlansResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: SubscriptionPlan[];
}

export interface SubscriptionTransactionsResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: SubscriptionTransaction[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalSubscriptions: number;
    limit: number;
  };
}

export interface SubscriptionCountsResponse {
  status: 'Success' | 'Failed';
  message: string;
  subscriptionCounts?: SubscriptionCounts;
}

export interface SingleSubscriptionResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: SubscriptionTransaction;
}

export interface SinglePlanResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: SubscriptionPlan;
}

// Parameters for API calls
export interface GetSubscriptionsParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  planType?: string;
}

export interface UpdatePlanParams {
  name: string;
  description: string;
  priceMonthly: number;
  priceYearly: number;
  features: string[];
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

export const subscriptionPlansApi = {
  // Get all subscription plans
  getSubscriptionPlans: async (): Promise<SubscriptionPlansResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/subscriptions/get-subscriptionplans`,
        // `${API_BASE_URL}/subscriptions/get-subscriptionplans`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching subscription plans:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch subscription plans',
        data: []
      };
    }
  },

  // Get all subscription history/transactions with filtering
  getAllSubscriptions: async (params: GetSubscriptionsParams = {}): Promise<SubscriptionTransactionsResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.status) queryParams.append('status', params.status);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.planType) queryParams.append('planType', params.planType);

      const response = await axios.get(
        `${API_BASE_URL}/subscriptions/history?${queryParams.toString()}`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching subscriptions:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch subscriptions',
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalSubscriptions: 0,
          limit: 10,
        },
      };
    }
  },

  // Get subscription counts by status
  getSubscriptionCounts: async (): Promise<SubscriptionCountsResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/subscriptions/counts`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching subscription counts:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch subscription counts',
        subscriptionCounts: {
          totalSubscriptions: 0,
          activeSubscriptions: 0,
          inactiveSubscriptions: 0
        }
      };
    }
  },

  // Get specific subscription by ID
  getSubscriptionById: async (subscriptionId: string): Promise<SingleSubscriptionResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/subscriptions/${subscriptionId}`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching subscription:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch subscription'
      };
    }
  },

  // Get specific plan by ID for editing
  getPlanById: async (planId: string): Promise<SinglePlanResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/subscriptions/plan/${planId}`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching plan:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch plan'
      };
    }
  },

  // Update subscription plan
  updatePlan: async (planId: string, params: UpdatePlanParams): Promise<SinglePlanResponse> => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/subscriptions/plan/${planId}`,
        params,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error updating plan:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to update plan'
      };
    }
  },
};